import React, { useEffect, useState } from 'react';
import apiClient from '../../../api/httpClient';
import endpoints from '../../../api/endpoints';
import {
  DEFAULT_CHART_SAMPLING_CONFIG,
  normalizeChartSamplingConfig,
} from '../../../utils/chartSamplingConfig';
import {
  CHART_SAMPLING_MODES,
  MIN_POINTS_PER_HOUR,
  CHART_SAMPLING_RANGES,
  normalizeChartSamplingMode,
  normalizePointsPerHour,
} from '../../../utils/chartSampling';

let cachedSamplingPolicy = null;
let samplingPolicyRequest = null;

const loadSamplingPolicy = async () => {
  if (cachedSamplingPolicy) return cachedSamplingPolicy;
  if (!samplingPolicyRequest) {
    samplingPolicyRequest = apiClient.get(endpoints.configuration.public())
      .then((response) => {
        cachedSamplingPolicy = normalizeChartSamplingConfig(response.data?.data?.chartSampling);
        return cachedSamplingPolicy;
      })
      .finally(() => { samplingPolicyRequest = null; });
  }
  return samplingPolicyRequest;
};

const ChartSamplingSettings = ({ edit, setEdit, t }) => {
  const labels = t?.settingsLabels || {};
  const samplingMode = normalizeChartSamplingMode(edit?.props?.samplingMode);
  const [samplingPolicy, setSamplingPolicy] = useState(
    cachedSamplingPolicy || DEFAULT_CHART_SAMPLING_CONFIG
  );

  useEffect(() => {
    let active = true;
    loadSamplingPolicy()
      .then((policy) => { if (active) setSamplingPolicy(policy); })
      .catch((error) => console.error('Failed to load chart sampling policy:', error));
    return () => { active = false; };
  }, []);

  const storedPointsByRange = edit?.props?.pointsPerHourByRange;
  const storedPointsPerHour = edit?.props?.pointsPerHour;
  const isUntouchedLegacyDefault = Number(storedPointsPerHour) === 60
    && CHART_SAMPLING_RANGES.every(({ key }) => Number(storedPointsByRange?.[key]) === 60);
  const explicitPointsByRange = isUntouchedLegacyDefault ? null : storedPointsByRange;
  const legacyPointsPerHour = isUntouchedLegacyDefault ? null : storedPointsPerHour;
  const pointsPerHourByRange = Object.fromEntries(CHART_SAMPLING_RANGES.map(({ key }) => {
    const configuredDefault = samplingPolicy.defaultPointsPerHourByRange[key];
    const configuredMaximum = samplingPolicy.maxPointsPerHourByRange[key];
    const rawValue = explicitPointsByRange?.[key] ?? legacyPointsPerHour ?? configuredDefault;
    return [key, Math.min(configuredMaximum, normalizePointsPerHour(rawValue, configuredDefault))];
  }));

  const updateSamplingProps = (nextProps) => {
    setEdit((prev) => ({
      ...prev,
      props: { ...prev.props, ...nextProps },
    }));
  };

  return (
    <div className="form-item">
      <label className="form-lbl" style={{ color: 'var(--header-color)' }}>
        {labels.samplingMode || 'Sampling Mode'}
      </label>

      <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
        <label style={{ display: 'flex', alignItems: 'center', gap: 8, color: 'var(--cell-color)' }}>
          <input type="radio" name={`chart-sampling-mode-${edit?.id || edit?.widgetId || 'widget'}`}
            checked={samplingMode === CHART_SAMPLING_MODES.DEFAULT}
            onChange={() => updateSamplingProps({ samplingMode: CHART_SAMPLING_MODES.DEFAULT })} />
          <span>{labels.samplingDefault || 'System default (adaptive)'}</span>
        </label>

        <label style={{ display: 'flex', alignItems: 'center', gap: 8, color: 'var(--cell-color)' }}>
          <input type="radio" name={`chart-sampling-mode-${edit?.id || edit?.widgetId || 'widget'}`}
            checked={samplingMode === CHART_SAMPLING_MODES.POINTS_PER_HOUR}
            onChange={() => updateSamplingProps({
              samplingMode: CHART_SAMPLING_MODES.POINTS_PER_HOUR,
              pointsPerHour: undefined,
              pointsPerHourByRange: undefined,
            })} />
          <span>{labels.samplingPointsPerHour || 'Points per hour'}</span>
        </label>
      </div>

      {samplingMode === CHART_SAMPLING_MODES.POINTS_PER_HOUR ? (
        <div style={{ marginTop: 10 }}>
          {CHART_SAMPLING_RANGES.map((range) => {
            const configuredDefault = samplingPolicy.defaultPointsPerHourByRange[range.key];
            const configuredMaximum = samplingPolicy.maxPointsPerHourByRange[range.key];
            const hasCustomValue = explicitPointsByRange?.[range.key] != null || legacyPointsPerHour != null;
            return (
              <label key={range.key} style={{ display: 'grid', gridTemplateColumns: '1fr 90px', gap: 8, alignItems: 'center', marginBottom: 6, color: 'var(--cell-color)' }}>
                <span>
                  {range.label}
                  <small style={{ display: 'block', opacity: 0.7, marginTop: 2 }}>
                    {hasCustomValue ? `Custom · Max ${configuredMaximum}` : `System default ${configuredDefault} · Max ${configuredMaximum}`}
                  </small>
                </span>
                <input type="number" className="add-column" min={MIN_POINTS_PER_HOUR}
                  max={configuredMaximum} step="1" value={pointsPerHourByRange[range.key]}
                  onChange={(event) => updateSamplingProps({
                    pointsPerHourByRange: {
                      ...(explicitPointsByRange || {}),
                      [range.key]: Math.min(
                        configuredMaximum,
                        normalizePointsPerHour(event.target.value, pointsPerHourByRange[range.key])
                      ),
                    },
                  })}
                  style={{ padding: '4px 10px', color: 'var(--cell--color)', caretColor: 'var(--cell--color)' }} />
              </label>
            );
          })}
          <span className="form-name-helper">
            {labels.pointsPerHourHelper || 'System defaults apply until you enter a custom value. Every value is restricted by its configured maximum.'}
          </span>
        </div>
      ) : null}
    </div>
  );
};

export default ChartSamplingSettings;
